import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { Modal, Input, SimpleSelect, Tabs, TabsList, TabsTrigger, TabsContent, Button } from '@/components/shared/ui';
import { FileSpreadsheet, X, Download } from 'lucide-react';
import { User } from '../../../types/admin';
import { formatDateShort } from '@/utils/dateUtils';
import { BULK_CREATE_GUEST_VALIDITY_DAYS_DEFAULT } from '@/utils/bulkCreateGuestValidity';
import { useDownloadBulkUploadTemplate } from '@/hooks/useUserManagement';
import { toast } from '@/hooks/use-toast';
interface LicenseInfo {
    hasDeviceLicense: boolean;
    maxUsers: number | null;
    currentUsers: number;
    availableSlots?: number;
}

export interface CreateGuestSaveContext {
    idPrefix?: string;
    namePrefix?: string;
    password?: string;
    validityDays?: number;
    uploadedFile?: File;
}

interface CreateGuestModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (guests: Partial<User>[], context: CreateGuestSaveContext) => void;
    licenseInfo?: LicenseInfo;
    skipUserCountCheck?: boolean;
}

export default function CreateGuestModal({ isOpen, onClose, onSave, licenseInfo, skipUserCountCheck = false }: CreateGuestModalProps): React.ReactElement {
    const { t, i18n } = useTranslation('admin');
    const [mode, setMode] = useState<'auto' | 'upload'>('auto');
    const [quantityInput, setQuantityInput] = useState<string>('10');
    const quantity = parseInt(quantityInput, 10) || 1;
    const [idPrefix, setIdPrefix] = useState<string>('guest');
    const [namePrefix, setNamePrefix] = useState<string>('jennie');
    const [duration, setDuration] = useState<number>(BULK_CREATE_GUEST_VALIDITY_DAYS_DEFAULT);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [password, setPassword] = useState<string>('guest1234');
    const [passwordError, setPasswordError] = useState<string>('');
    const downloadTemplateMutation = useDownloadBulkUploadTemplate();

    // per_device 라이선스이거나 skipUserCountCheck이면 사용자 수 제한 없음
    const effectiveNoUserLimit = skipUserCountCheck || licenseInfo?.hasDeviceLicense;

    const validatePassword = (pw: string): boolean => {
        if (pw.length < 8) {
            setPasswordError(t('createGuestModal.passwordMinLength'));
            return false;
        }
        const hasLetter = /[a-zA-Z]/.test(pw);
        const hasNumber = /[0-9]/.test(pw);
        if (!hasLetter || !hasNumber) {
            setPasswordError(t('createGuestModal.passwordFormat'));
            return false;
        }
        setPasswordError('');
        return true;
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (ext && ['xlsx', 'xls', 'csv'].includes(ext)) {
                setUploadedFile(file);
            } else {
                alert(t('createGuestModal.invalidFileType'));
            }
        }
    };

    const removeFile = () => {
        setUploadedFile(null);
    };

    const downloadGuestTemplate = async () => {
        try {
            const response = await downloadTemplateMutation.mutateAsync();
            if (response.downloadUrl) {
                // presigned URL로 파일 다운로드
                const link = document.createElement('a');
                link.href = response.downloadUrl;
                link.download = 'guest_upload_template.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error('템플릿 다운로드 실패:', error);
            toast({
                title: t('createGuestModal.templateDownloadFailed', { defaultValue: '템플릿 다운로드 실패' }),
                description: error instanceof Error ? error.message : t('createGuestModal.templateDownloadError', { defaultValue: '템플릿 다운로드 중 오류가 발생했습니다.' }),
                variant: 'destructive',
            });
        }
    };

    const resetForm = () => {
        setQuantityInput('10');
        setIdPrefix('guest');
        setNamePrefix('jennie');
        setDuration(BULK_CREATE_GUEST_VALIDITY_DAYS_DEFAULT);
        setPassword('guest1234');
        setPasswordError('');
        setUploadedFile(null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validatePassword(password)) {
            return;
        }

        if (mode === 'auto') {
            const guests: Partial<User>[] = [];
            const today = new Date();
            const expirationDate = new Date(today);
            expirationDate.setDate(today.getDate() + duration);
            const expirationDateString = formatDateShort(expirationDate.toISOString(), i18n.language);

            for (let i = 1; i <= quantity; i++) {
                guests.push({
                    id: `${idPrefix}${i}`,
                    name: `${namePrefix}${i}`,
                    email: '',
                    accountType: '게스트',
                    role: 'Guest',
                    registeredDate: formatDateShort(today.toISOString(), i18n.language),
                    lastLogin: undefined,
                    status: 'active',
                    expirationDate: expirationDateString,
                    password,
                });
            }
            onSave(guests, { idPrefix, namePrefix, password, validityDays: duration });
            resetForm();
            onClose();
            return;
        }

        // 파일 업로드 모드
        if (!uploadedFile) {
            alert(t('createGuestModal.selectFile'));
            return;
        }

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const data = ev.target?.result;
                const wb = XLSX.read(data, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

                // 첫 행 헤더 파악 — loginId/id/ID, pw/password, realName/name
                const header = (rows[0] as string[]).map((h) => String(h ?? '').trim().toLowerCase());
                const idxId = ['loginid', 'id'].findIndex((k) => header.includes(k));
                const idxPw = ['pw', 'password'].findIndex((k) => header.includes(k));
                const idxName = ['realname', 'name'].findIndex((k) => header.includes(k));
                const colId = idxId >= 0 ? header.indexOf(['loginid', 'id'][idxId]) : -1;
                const colPw = idxPw >= 0 ? header.indexOf(['pw', 'password'][idxPw]) : -1;
                const colName = idxName >= 0 ? header.indexOf(['realname', 'name'][idxName]) : -1;

                const today = new Date();
                const expirationDate = new Date(today);
                expirationDate.setDate(today.getDate() + duration);
                const expirationDateString = formatDateShort(expirationDate.toISOString(), i18n.language);

                const guests: Partial<User>[] = [];
                for (let r = 1; r < rows.length; r++) {
                    const row = rows[r] as unknown[];
                    const guestId = colId >= 0 ? String(row[colId] ?? '').trim() : '';
                    const guestPw = colPw >= 0 ? String(row[colPw] ?? '').trim() : password;
                    const guestName = colName >= 0 ? String(row[colName] ?? '').trim() : '';
                    if (!guestId) continue;
                    guests.push({
                        id: guestId,
                        name: guestName || guestId,
                        email: '',
                        accountType: '게스트',
                        role: 'Guest',
                        registeredDate: formatDateShort(today.toISOString(), i18n.language),
                        lastLogin: undefined,
                        status: 'active',
                        expirationDate: expirationDateString,
                        password: guestPw || password,
                    });
                }

                if (guests.length === 0) {
                    alert(t('createGuestModal.parseNoRows'));
                    return;
                }

                onSave(guests, { password, validityDays: duration, uploadedFile });
                resetForm();
                onClose();
            } catch {
                alert(t('createGuestModal.parseFailed'));
            }
        };
        reader.readAsBinaryString(uploadedFile);
    };

    const footer = (
        <div className="flex justify-end items-center gap-2 w-full shrink-0">
            <Button variant="secondary" onClick={onClose} type="button">
                {t('createGuestModal.cancel')}
            </Button>
            <Button variant="primary" size="md" type="submit" form="create-guest-form">
                {t('createGuestModal.confirm')}
            </Button>
        </div>
    );

    // 수량 상한: availableSlots가 양수일 때만 제한, 나머지(제한없음·미전달·0 이하)는 자유입력(최대 9999)
    const rawAvailableSlots = licenseInfo?.availableSlots;
    const hasPositiveSlotLimit = !effectiveNoUserLimit && rawAvailableSlots != null && rawAvailableSlots > 0;
    const maxAllowedQuantity = hasPositiveSlotLimit ? rawAvailableSlots : 9999;
    const slotsExhausted = false; // 수량 입력 자체는 항상 허용 (제출 시 서버가 검증)

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('createGuestModal.title')} footer={footer} size="medium">
            <form id="create-guest-form" onSubmit={handleSubmit}  className="space-y-4">
                <Tabs value={mode} onValueChange={(value: string) => setMode(value as 'auto' | 'upload')}>
                    <TabsList className="w-full grid grid-cols-2 rounded-lg bg-gray-100 p-1">
                        <TabsTrigger value="auto" className="rounded-md">{t('createGuestModal.tabAuto')}</TabsTrigger>
                        <TabsTrigger value="upload" className="rounded-md">{t('createGuestModal.tabUpload')}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="auto" className="min-h-[360px] mt-4 px-3 py-2 space-y-4">
                        <h4 className="text-center mb-4 text-sm text-gray-600 whitespace-pre-line">{t('createGuestModal.autoDescription')}</h4>

                        <div className="flex items-end gap-2">
                            <div className="space-y-2">
                                <Input
                                    type="text"
                                    label={t('createGuestModal.idPrefix')}
                                    value={idPrefix}
                                    onChange={(e) => {
                                        // 영문·숫자·언더스코어·하이픈만 허용 (한글 등 비ASCII 차단)
                                        const val = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
                                        setIdPrefix(val);
                                    }}
                                    placeholder={t('createGuestModal.idPrefixPlaceholder')}
                                    className="w-48"
                                />
                            </div>
                            <p className="text-sm text-gray-500 pb-2 shrink-0">{t('createGuestModal.idPrefixExample')}</p>
                        </div>
                        <div className="flex items-end gap-2">
                            <div className="space-y-2">
                                <Input
                                    type="text"
                                    label={t('createGuestModal.namePrefix')}
                                    value={namePrefix}
                                    onChange={(e) => setNamePrefix(e.target.value)}
                                    placeholder={t('createGuestModal.namePrefixPlaceholder')}
                                    className="w-48"
                                />
                            </div>
                            <p className="text-sm text-gray-500 pb-2 shrink-0">{t('createGuestModal.namePrefixExample')}</p>
                        </div>
                        <div className="flex items-start gap-2">
                            <div className="space-y-1 flex-1">
                                <label className="block text-sm font-medium text-gray-600 ml-0.5">
                                    {t('createGuestModal.password')}{' '}
                                    <span className="text-xs text-gray-400 font-normal">
                                        ({t('createGuestModal.passwordRequirement')})
                                    </span>
                                </label>
                                <Input
                                    type="text"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (passwordError) {
                                            validatePassword(e.target.value);
                                        }
                                    }}
                                    onBlur={() => validatePassword(password)}
                                    required
                                    placeholder={t('createGuestModal.passwordPlaceholder')}
                                    className="w-48"
                                />
                                {passwordError && (
                                    <p className="text-xs text-red-500">{passwordError}</p>
                                )}
                            </div>
                            <p className="text-sm text-gray-500 pt-7 shrink-0">{t('createGuestModal.passwordHint')}</p>
                        </div>
                        <div className="flex items-end gap-2">
                            <div className="space-y-2">
                                <Input
                                    type="number"
                                    label={t('createGuestModal.quantity')}
                                    value={quantityInput}
                                    disabled={slotsExhausted}
                                    onChange={(e) => {
                                        if (slotsExhausted) return;
                                        const raw = e.target.value;
                                        // 중간 입력(빈값, 0) 허용 — onBlur에서 보정
                                        if (raw === '' || raw === '0') {
                                            setQuantityInput(raw);
                                            return;
                                        }
                                        const parsed = parseInt(raw, 10);
                                        if (isNaN(parsed)) return;
                                        const clamped = Math.min(Math.max(1, parsed), maxAllowedQuantity);
                                        setQuantityInput(String(clamped));
                                    }}
                                    onBlur={() => {
                                        if (slotsExhausted) return;
                                        const parsed = parseInt(quantityInput, 10);
                                        if (isNaN(parsed) || parsed < 1) setQuantityInput('1');
                                        else if (parsed > maxAllowedQuantity) setQuantityInput(String(maxAllowedQuantity));
                                    }}
                                    min={1}
                                    max={maxAllowedQuantity}
                                    required
                                    className="w-48"
                                />
                            </div>
                            <p className="text-sm text-brand-500 pb-2 shrink-0">{t('createGuestModal.availableSlots', { count: maxAllowedQuantity })}</p>
                        </div>
                    </TabsContent>

                    <TabsContent value="upload" className="min-h-[360px] mt-4 py-2 space-y-6">
                        <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-700 mb-2">{t('createGuestModal.howToFile')}</div>
                            <div className="flex flex-col items-center bg-gray-50 rounded-lg p-4 gap-2">
                                <p className="text-sm text-gray-700 ml-2">{t('createGuestModal.fileInstruction')}</p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={downloadGuestTemplate}
                                    className="inline-flex items-center gap-1 text-sm"
                                >
                                    <Download size={16} />
                                    {t('createGuestModal.templateDownload')}
                                </Button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <label className="text-sm font-medium text-gray-700 shrink-0">
                                    {t('createGuestModal.fileAttachment')} <span className="text-red-500">*</span>
                                </label>
                                {licenseInfo && (
                                    <span className="text-sm text-brand-600 flex items-center gap-1.5 shrink-0">
                                        {t('createGuestModal.availableCount')}{' '}
                                        {effectiveNoUserLimit ? (
                                            <span className="text-brand-600">{t('createGuestModal.unlimited')}</span>
                                        ) : (
                                            <span className="text-brand-600">{licenseInfo.availableSlots ?? 0}{t('createGuestModal.availableCountUnit')}</span>
                                        )}
                                    </span>
                                )}
                            </div>
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-brand-500 hover:bg-gray-50/50 transition-colors"
                                onClick={() => document.getElementById('guest-file-input')?.click()}
                            >
                                <input
                                    id="guest-file-input"
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <FileSpreadsheet size={32} className="mx-auto mb-2 text-gray-400" />
                                <p className="text-sm text-gray-600">
                                    {t('createGuestModal.clickToSelect')}<br />
                                    {!uploadedFile && <span className="text-xs text-gray-500 block mt-1">{t('createGuestModal.noFileSelected')}</span>}
                                    <span className="text-xs text-gray-500 block mt-0.5">{t('createGuestModal.supportedFormats')}</span>
                                </p>
                            </div>
                            {uploadedFile && (
                                <div className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2 mt-2">
                                    <span className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
                                        <FileSpreadsheet size={16} className="shrink-0 text-gray-500" />
                                        <span className="truncate">{uploadedFile.name}</span>
                                    </span>
                                    <button
                                        type="button"
                                        onClick={removeFile}
                                        className="p-1.5 rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-700 shrink-0"
                                        aria-label={t('createGuestModal.ariaRemoveFile')}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    <div className="flex items-end gap-2 px-3 py-2 pt-2 border-t border-gray-100">
                        <div className="w-48 shrink-0">
                            <SimpleSelect
                                label={t('createGuestModal.expiration')}
                                value={String(duration)}
                                onChange={(e) => setDuration(Number(e.target.value) || 30)}
                            >
                                <option value="1">{t('createGuestModal.days1')}</option>
                                <option value="7">{t('createGuestModal.days7')}</option>
                                <option value="30">{t('createGuestModal.days30')}</option>
                                <option value="60">{t('createGuestModal.days60')}</option>
                                <option value="90">{t('createGuestModal.days90')}</option>
                                <option value="365">{t('createGuestModal.months12')}</option>
                            </SimpleSelect>
                        </div>
                        <p className="text-sm text-red-500 pb-2 shrink-0 whitespace-pre-line">{t('createGuestModal.expirationWarning')}</p>
                    </div>
                </Tabs>
            </form>
        </Modal>
    );
}
